import { moment } from '@seiue/moment'
import { ColumnType } from '@seiue/ui'
import { isArray } from '@seiue/util'
import { FormInstance } from 'antd'
import { saveAs } from 'file-saver'
import { AnyType } from 'packages/types'
import { momentToStr } from 'packages/utils'
import * as XLSX from 'xlsx'

import { isRangeObjectValue } from '../DatePickerRange/utils'

import { IParams, NumberRangeValue } from './types'

// 弃用2023-4-23 时间类型已无需判断是moment格式，统一使用[string, string]格式
export const arrayParamsFormat = <T>(key: string, arr: T[]) => {
  const newParams: IParams = {}
  if (arr.length === 2 && moment.isMoment(arr[0]) && moment.isMoment(arr[1])) {
    newParams[`${key}Egt`] = momentToStr(arr[0])
    newParams[`${key}Elt`] = momentToStr(arr[1])
  } else {
    newParams[`${key}In`] = arr.join(',')
  }
  return newParams
}

export const valueFormatToText = (val: any) => {
  if (isArray(val)) {
    return val.join(',')
  }
  if (isRangeObjectValue(val)) {
    return `${val.lt}~${val.rt}`
  }
  if (isNumberRange(val)) {
    const egt = typeof val.egt === 'number' ? val.egt : '未设置'
    const elt = typeof val.elt === 'number' ? val.elt : '未设置'
    return `${egt}~${elt}`
  }
  return val
}

/**
 * 时间范围筛选时格式转化
 * @param key - 筛选时默认的key
 * @param val - 原始值
 * @param isField - 是否是自定义字段
 * @returns 格式化后的数据
 */
export const paramsFormat = (
  key: string,
  val: any,
  isField?: boolean,
  inLikeArr?: string[],
  accurateMatch?: boolean
) => {
  const newParams: IParams = {}
  if (isArray(val)) {
    const newKey = isField ? `field.${key}In` : `${key}In`
    newParams[newKey] = val.join(',')
  } else if (isRangeObjectValue(val)) {
    const newKeyLeft = isField ? `field.${key}Egt` : `${key}Egt`
    const newKeyRight = isField ? `field.${key}Elt` : `${key}Elt`
    newParams[newKeyLeft] = val.lt
    newParams[newKeyRight] = val.rt
    return newParams
  } else if (isNumberRange(val)) {
    const newKeyLeft = isField ? `field.${key}Egt` : `${key}Egt`
    const newKeyRight = isField ? `field.${key}Elt` : `${key}Elt`
    newParams[newKeyLeft] = val.egt
    newParams[newKeyRight] = val.elt
    return newParams
  } else if (typeof val === 'string' && inLikeArr?.includes(key)) {
    const newKey = /，|,/.test(val) ? `${key}In` : `${key}Like`
    newParams[newKey] = val.replace(/，|,/g, ',')
  } else {
    const newKey =
      accurateMatch === true
        ? isField
          ? `field.${key}`
          : `${key}`
        : isField
        ? `field.${key}Like`
        : `${key}Like`
    newParams[newKey] = val
  }
  return newParams
}

export const onpressEnterSearch = (
  e: React.KeyboardEvent<HTMLInputElement>,
  callback?: () => void
) => {
  if (e.keyCode === 13) {
    callback && callback()
  }
}

// 弃用
export const dateRangeValueFormat = <T>(
  val: T,
  name: string,
  form?: FormInstance
) => {
  if (
    Array.isArray(val) &&
    val.length === 2 &&
    moment.isMoment(val[0]) &&
    moment.isMoment(val[1])
  ) {
    form?.setFieldValue(name, [val[0].startOf('day'), val[1].startOf('day')])
  }
}

export const isNumberRange = (val: unknown) => {
  return (
    typeof val === 'object' &&
    val !== null &&
    (typeof (val as NumberRangeValue).egt === 'number' ||
      typeof (val as NumberRangeValue).egt === 'undefined') &&
    (typeof (val as NumberRangeValue).elt === 'number' ||
      typeof (val as NumberRangeValue).elt === 'undefined')
  )
}

type DataItem = Record<string, AnyType>

/**
 * 将数据导出为 Excel
 *
 * @param params - 参数
 * @param params.columns 列定义（包含 title, dataIndex 等）
 * @param params.data 原始数据数组
 * @param params.fileName 导出的文件名
 */
export const exportToExcel = ({
  columns,
  data,
  fileName,
}: {
  columns: (ColumnType<AnyType> & {
    exportText?: (value: AnyType, record: AnyType) => string
  })[]
  data: DataItem[]
  fileName: string
}) => {
  // 1. 构建表头
  const headers = columns.map((col) => col.title)

  // 2. 构建每一行数据
  const rows = data.map((record) => {
    return columns.map((col) => {
      const value = record[String(col.dataIndex ?? '')]

      // 优先使用 exportText 函数
      if (typeof col.exportText === 'function') {
        return col.exportText(value, record)
      }

      if (typeof value === 'string' || typeof value === 'number') {
        return value
      }

      return '-'
    })
  })

  // 3. 合并表头和数据
  const sheetData = [headers, ...rows]

  // 4. 创建工作簿和表格
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')

  // 5. 写入并触发下载
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' })
  saveAs(blob, `${fileName}.xlsx`)
}

/**
 * 处理 input 值的筛选参数，将字符串里的 ','、'，'、';'、'；'、'、'、'\n'、'\t'替换为 ','
 *
 * @param param - 参数
 * @param param.value - 值
 * @returns string
 */
export const handleInputValue = (value: string) => {
  return value
    .replace(/，|，|;|；|、|\n|\t/g, ',')
    .split(',')
    .filter(Boolean)
    .join(',')
}

/**
 * 检查字符串里是否有 ','、'，'、';'、'；'和换行符\n
 *
 * @param value - 值
 * @returns boolean
 */
export const hasComma = (value: string) => {
  return /，|，|;|；|,|、|\n|\t/.test(value)
}
